/**
 * The keyboard, plucked, bowed and vocal presets, in score order.
 *
 * The other half of the instrument table; see {@link ./instruments-wind} for the first.
 */

import { ALTO, BASS, TREBLE } from "../document/types";
import { AT_PITCH } from "../document/pitch";
import type { Transposition } from "../document/pitch";
import type { InstrumentPreset } from "./instruments";
import { BASS_TUNING, GUITAR_TUNING, UKULELE_TUNING } from "../document/tab";

const octave = (octaveChange: number): Transposition => ({
  chromatic: 0,
  diatonic: 0,
  octaveChange,
});

/**
 * The clef a tab staff carries.
 *
 * The line is 5 whatever the staff has for lines: MusicXML's TAB clef is placed the way a
 * G clef is, and a reader of the file takes the strings from `<staff-tuning>` rather than
 * from where the clef sits.
 */
const TAB_CLEF = { sign: "TAB" as const, line: 5, octaveChange: 0 };

/** Every instrument a part can be made from, in score order within each family. */

export const STRINGS_KEYBOARDS_AND_VOICES: readonly InstrumentPreset[] = [
  {
    id: "piano",
    name: "Piano",
    abbreviation: "Pno.",
    family: "keyboard",
    clefs: [TREBLE, BASS],
    transposition: AT_PITCH,
    sound: "keyboard.piano",
    midiProgram: 0,
    range: { low: 21, high: 108 },
  },
  {
    id: "celesta",
    name: "Celesta",
    abbreviation: "Cel.",
    family: "keyboard",
    clefs: [TREBLE, BASS],
    transposition: octave(1),
    sound: "keyboard.celesta",
    midiProgram: 8,
    range: { low: 48, high: 84 },
  },
  {
    id: "organ",
    name: "Organ",
    abbreviation: "Org.",
    family: "keyboard",
    clefs: [TREBLE, BASS],
    transposition: AT_PITCH,
    sound: "keyboard.organ",
    midiProgram: 19,
    range: { low: 36, high: 96 },
  },

  {
    id: "harp",
    name: "Harp",
    abbreviation: "Hp.",
    family: "plucked",
    clefs: [TREBLE, BASS],
    transposition: AT_PITCH,
    sound: "pluck.harp",
    midiProgram: 46,
    range: { low: 24, high: 104 },
  },
  {
    id: "guitar",
    name: "Guitar",
    abbreviation: "Gtr.",
    family: "plucked",
    clefs: [TREBLE],
    transposition: octave(-1),
    sound: "pluck.guitar",
    midiProgram: 24,
    range: { low: 52, high: 88 },
  },
  {
    id: "guitar-tab",
    name: "Guitar (tab)",
    abbreviation: "Gtr.",
    family: "plucked",
    clefs: [TAB_CLEF],
    transposition: octave(-1),
    sound: "pluck.guitar",
    midiProgram: 24,
    range: { low: 52, high: 88 },
    tuning: GUITAR_TUNING,
    lines: 6,
  },
  {
    id: "bass-guitar",
    name: "Bass guitar",
    abbreviation: "B. Gtr.",
    family: "plucked",
    clefs: [BASS],
    transposition: octave(-1),
    sound: "pluck.bass-electric",
    midiProgram: 33,
    range: { low: 40, high: 72 },
  },
  {
    id: "bass-guitar-tab",
    name: "Bass guitar (tab)",
    abbreviation: "B. Gtr.",
    family: "plucked",
    clefs: [TAB_CLEF],
    transposition: octave(-1),
    sound: "pluck.bass-electric",
    midiProgram: 33,
    range: { low: 40, high: 72 },
    tuning: BASS_TUNING,
    lines: 4,
  },
  {
    id: "ukulele",
    name: "Ukulele",
    abbreviation: "Uke.",
    family: "plucked",
    clefs: [TREBLE],
    transposition: AT_PITCH,
    sound: "pluck.ukulele",
    midiProgram: 24,
    range: { low: 60, high: 86 },
  },
  {
    id: "ukulele-tab",
    name: "Ukulele (tab)",
    abbreviation: "Uke.",
    family: "plucked",
    clefs: [TAB_CLEF],
    transposition: AT_PITCH,
    sound: "pluck.ukulele",
    midiProgram: 24,
    range: { low: 60, high: 86 },
    tuning: UKULELE_TUNING,
    lines: 4,
  },

  {
    id: "violin",
    name: "Violin",
    abbreviation: "Vln.",
    family: "strings",
    clefs: [TREBLE],
    transposition: AT_PITCH,
    sound: "strings.violin",
    midiProgram: 40,
    range: { low: 55, high: 100 },
  },
  {
    id: "viola",
    name: "Viola",
    abbreviation: "Vla.",
    family: "strings",
    clefs: [ALTO],
    transposition: AT_PITCH,
    sound: "strings.viola",
    midiProgram: 41,
    range: { low: 48, high: 88 },
  },
  {
    id: "cello",
    name: "Violoncello",
    abbreviation: "Vc.",
    family: "strings",
    clefs: [BASS],
    transposition: AT_PITCH,
    sound: "strings.cello",
    midiProgram: 42,
    range: { low: 36, high: 81 },
  },
  {
    id: "double-bass",
    name: "Double bass",
    abbreviation: "Cb.",
    family: "strings",
    clefs: [BASS],
    transposition: octave(-1),
    sound: "strings.contrabass",
    midiProgram: 43,
    range: { low: 40, high: 72 },
  },

  {
    id: "soprano",
    name: "Soprano",
    abbreviation: "S.",
    family: "voice",
    clefs: [TREBLE],
    transposition: AT_PITCH,
    sound: "voice.soprano",
    midiProgram: 52,
    range: { low: 60, high: 81 },
  },
  {
    id: "alto",
    name: "Alto",
    abbreviation: "A.",
    family: "voice",
    clefs: [TREBLE],
    transposition: AT_PITCH,
    sound: "voice.alto",
    midiProgram: 52,
    range: { low: 53, high: 74 },
  },
  {
    id: "tenor",
    name: "Tenor",
    abbreviation: "T.",
    family: "voice",
    clefs: [{ ...TREBLE, octaveChange: -1 }],
    transposition: AT_PITCH,
    sound: "voice.tenor",
    midiProgram: 52,
    range: { low: 48, high: 69 },
  },
  {
    id: "bass-voice",
    name: "Bass",
    abbreviation: "B.",
    family: "voice",
    clefs: [BASS],
    transposition: AT_PITCH,
    sound: "voice.bass",
    midiProgram: 52,
    range: { low: 40, high: 64 },
  },
];
